import { useQuery } from "@tanstack/react-query";
import { odataFetch } from "../../lib/odata-fetch";
import { QGetAllTasks2 } from "../../odata/task/QTask";
import { GetAllTasksResponse } from "../../odata/task/TaskModel";
import { useMachine } from "../machine/use-machine";
import { useMTUs } from "../mtu/use-mtus";
import { useWorkOrder } from "../work-order/use-work-order";
import { useWorkOrderOperation } from "../work-order/use-work-order-operation";
import { useState } from "react";
import { taskKeys } from "./task-keys";

export function useTasks() {
  const { machine } = useMachine();
  const { workOrderOperation } = useWorkOrderOperation();
  const { workOrder } = useWorkOrder();
  const { selected } = useMTUs();
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>();

  const { data, ...query } = useQuery({
    queryKey: taskKeys.listAll(
      machine?.EquipmentNId,
      workOrderOperation?.NId,
      workOrder?.NId,
      workOrder?.FinalMaterial.Material.UId
    ),
    queryFn: ({ meta }) =>
      fetchTasks(
        {
          machineId: machine!.EquipmentNId!,
          workOrderOperationNId: workOrderOperation!.NId!,
          workOrderNId: workOrder!.NId!,
          materialUId: workOrder!.FinalMaterial.Material.UId!,
        },
        meta!.token!
      ),
    enabled: ({ meta }) =>
      !!meta?.token &&
      !!machine?.EquipmentNId &&
      !!workOrderOperation?.NId &&
      !!workOrder?.NId &&
      !!workOrder?.FinalMaterial.Material.UId,
  });

  const tasks = data?.value?.filter(
    (x) =>
      x.Description ===
      selected?.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code
  );
  const incompleteTasks = tasks?.filter(
    (x) => x.Status?.StatusNId !== "Completed"
  );

  const isEmpty = query.isSuccess && !tasks?.length;

  return {
    allTasks: data?.value,
    tasks,
    incompleteTasks,
    selectedTask:
      (selectedTaskId &&
        incompleteTasks?.find((x) => x.Id === selectedTaskId)) ||
      incompleteTasks?.[0],
    isEmpty,
    setSelectedTask: setSelectedTaskId,
    ...query,
  };
}

type Payload = {
  machineId: string;
  workOrderOperationNId: string;
  workOrderNId: string;
  materialUId: string;
};

export type TaskTypes =
  | "GF_EngraverTaskDefinition"
  | "GF_PackingTaskDefinition"
  | "GF_MarkingTaskDefinition";

export type Task = GetAllTasksResponse & {
  TaskDefinitionNId: TaskTypes;
};

export async function fetchTasks(
  { machineId, workOrderOperationNId, workOrderNId, materialUId }: Payload,
  token: string
) {
  const path = new QGetAllTasks2().buildUrl({
    function: {
      TaskId: null,
      EquipmentNId: machineId,
      TaskContextUserFieldValues: [
        {
          UserFieldNId: "WorkOrderOperationNId",
          UserFieldValue: workOrderOperationNId,
        },
        { UserFieldNId: "WorkOrderNId", UserFieldValue: workOrderNId },
        { UserFieldNId: "WorkOrderStepNId", UserFieldValue: null },
        { UserFieldNId: "DM_MaterialUId", UserFieldValue: materialUId },
      ],
    },
  });

  return odataFetch<Task>({ path, token, service: "Task" });
}
